import React, { RefObject } from 'react';
import classNames from 'classnames';

import appActions from 'common/support/applicationActions';
import device from 'common/support/device';
import focusManager from 'support/focus/FocusManager';
import FocusGroup from 'components/focus/FocusGroup';
import withRestoration, { WithRestorationProps, RestorationState } from 'components/withRestoration';

import { getAppScalingFactor } from 'support/scalingHelper';
import { selectItems } from 'selectors/list';
import textToSpeechManager from 'support/tts/TextToSpeechManager';

import styles from './List.module.scss';

// We need to manually calculate the scaling factor (app width / 1920) since certain measurements
// are not scaled appropriately even though the element is affected by transform: scale
// getBoundingClientRect values are scaled automatically, whereas offset (ie. offsetTop/offsetLeft)
// and buffers need to be scaled manually
const scaling = getAppScalingFactor();

const ScrollDirection = {
  LEFT: 'left',
  RIGHT: 'right',
} as const;

type ScrollDirectionType = typeof ScrollDirection[keyof typeof ScrollDirection];

export interface ListDataItem {
  id: string | number;
  [key: string]: unknown;
}

interface ListState {
  isAnimating: boolean;
  left: number;
  startIndex: number;
  endIndex: number;
}

interface ListOwnProps {
  renderItem: (item: ListDataItem, index: number, ref?: RefObject<HTMLElement>) => React.ReactNode;
  data: ListDataItem[];
  buffer: number;
  className?: string | null;
  focusId?: string | number | null;
  onFocusDelegated?: (index: number) => void;
  onFocusIndexChanged?: (index: number, ...rest: unknown[]) => void;
  peekRatio?: number;
  ttsEndOfNavigation?: string | null;
  onEndOfNavigationOverride?: (index: number) => void | boolean;
  restorationId?: string;
}

type ListProps = ListOwnProps & WithRestorationProps;

class List extends React.PureComponent<ListProps, ListState> {
  static defaultProps: Partial<ListOwnProps> = {
    className: null,
    focusId: null,
    onFocusDelegated: () => {},
    onFocusIndexChanged: () => {},
    peekRatio: 0.2,
    ttsEndOfNavigation: null,
    onEndOfNavigationOverride: () => {
      return false;
    },
  };

  state: ListState = {
    isAnimating: false,
    left: 0,
    startIndex: 0,
    endIndex: 0,
  };

  listViewport = React.createRef<HTMLDivElement>();

  focusGroup = React.createRef<FocusGroup>();

  listItem = React.createRef<HTMLDivElement>();

  focusedIndex = 0;

  itemWidth: number | null = null;

  timeout: ReturnType<typeof setTimeout> | null = null;

  focusId: string | number | null = null;

  constructor(props: ListProps) {
    super(props);
    const { restorationState, data } = props;
    this.focusId = props.focusId || focusManager.getNewUniqueId();
    this.state.endIndex = props.buffer;
    if (restorationState && data) {
      const {
        endIndex: oldEndIndex,
        startIndex: oldStartIndex,
      } = restorationState;
      let newEndIndex = oldEndIndex;
      let newStartIndex = oldStartIndex;
      if ((oldEndIndex as number) > data.length - 1) {
        newEndIndex = data.length - 1;
        const amountToShift = (oldEndIndex as number) - newEndIndex;
        newStartIndex = Math.max(0, (oldStartIndex as number) - amountToShift);
      }
      this.state = {
        ...(restorationState as unknown as ListState),
        startIndex: (newStartIndex as number),
        endIndex: (newEndIndex as number),
      };
    }
  }

  componentDidMount(): void {
    const { data } = this.props;
    focusManager.addFocusChangedListener(this.onFocusChanged);
    if (data && this.listItem && this.listItem.current) {
      this.itemWidth = this.listItem.current.getBoundingClientRect().width;
    }
  }

  componentDidUpdate(prevProps: ListProps, prevState: ListState): void {
    const { data, setRestorationState } = this.props;

    if (!prevProps.data && data && this.listItem && this.listItem.current) {
      this.itemWidth = this.listItem.current.getBoundingClientRect().width;
    }

    if (prevState !== this.state && this.state) {
      setRestorationState(this.state as unknown as RestorationState);
    }
  }

  componentWillUnmount(): void {
    focusManager.removeFocusChangedListener(this.onFocusChanged);
  }

  onFocusChanged = (): void => {
    if (!window || !focusManager.isFocusAncestor(this.focusId)) {
      return;
    }

    const focusedEl = focusManager.getFocusableElement(
      focusManager.currentFocusId as string,
    ) as HTMLElement;
    const {
      left: rectLeft,
      right: rectRight,
    } = focusedEl.getBoundingClientRect();

    const lastFocusedIndex = this.focusedIndex;
    const offsetLeft = focusedEl.offsetLeft * scaling;
    const itemWidth = rectRight - rectLeft;
    const { peekRatio = 0.2 } = this.props;
    const margin = itemWidth * peekRatio;

    this.setState(
      {
        isAnimating: true,
        left: Math.min(0, -offsetLeft - margin),
      },
      () => {
        if (!this.timeout) {
          this.timeout = setTimeout(() => {
            this.timeout = null;
            this.handleClipping(ScrollDirection.RIGHT, lastFocusedIndex);
          }, 300);
        }
      },
    );
  };

  handleClipping = (_scrollDirection: ScrollDirectionType, lastFocusedIndex: number): void => {
    const { buffer } = this.props;
    const { startIndex, endIndex, left } = this.state;

    // Need to add these indexes together since the FocusGroup is only
    // receiving a subset of items
    const focusedIndex = lastFocusedIndex + startIndex;
    const newStartIndex = Math.max(0, focusedIndex - 1);
    const newEndIndex = newStartIndex + buffer + 1;

    if (
      (newStartIndex !== startIndex || newEndIndex !== endIndex) &&
      this.itemWidth
    ) {
      const amountToClip = Math.abs(newStartIndex - startIndex);
      const focusedEl = focusManager.getFocusableElement(
        focusManager.currentFocusId as string,
      ) as HTMLElement;
      const offsetLeft = focusedEl.offsetLeft * scaling;
      const offsetright =
        this.itemWidth - offsetLeft - focusedEl.offsetWidth * scaling;
      this.setState(
        {
          isAnimating: false,
          startIndex: newStartIndex,
          endIndex: newEndIndex,
          left:
            newStartIndex > startIndex
              ? left + (offsetLeft / lastFocusedIndex) * amountToClip
              : left - (this.itemWidth - offsetright) * amountToClip,
        },
        () => {
          if (this.focusGroup && this.focusGroup.current) {
            this.focusGroup.current.resetFocusIndex();
          }
        },
      );
    }
  };

  onFocusIndexOverride = (action: string, currentIndex: number): number | 'handled' | false => {
    let newIndex: number | false = false;

    switch (action) {
      case appActions.moveLeft:
        newIndex = currentIndex - 1;
        break;
      case appActions.moveRight:
        newIndex = currentIndex + 1;
        break;
      default:
        newIndex = false;
    }

    if (action !== appActions.moveLeft && action !== appActions.moveRight) {
      return newIndex;
    }

    const {
      left: viewportLeft,
      right: viewportRight,
    } = this.listViewport.current?.getBoundingClientRect() ?? {};

    const listFocusChildren = focusManager.getFocusableChildren(this.focusId);
    const newChild = listFocusChildren?.[newIndex as number];

    if (newChild) {
      const {
        left: newFocusLeft,
        right: newFocusRight,
      } = newChild.getBoundingClientRect();

      if (
        this.timeout &&
        (newFocusLeft < (viewportLeft as number) || newFocusRight > (viewportRight as number))
      ) {
        return 'handled';
      }
    }

    return newIndex;
  };

  onEndOfNavigation = (): void => {
    const { data, onEndOfNavigationOverride } = this.props;
    const { startIndex } = this.state;
    const index = this.focusedIndex + startIndex;
    if (index === 0 || index === data.length - 1) {
      const { ttsEndOfNavigation } = this.props;
      textToSpeechManager.add(ttsEndOfNavigation);
      textToSpeechManager.speak();
    }
    if (onEndOfNavigationOverride) {
      onEndOfNavigationOverride(index);
    }
  };

  onFocusIndexChanged = (index: string | number, ...rest: unknown[]): void => {
    const { onFocusIndexChanged } = this.props;
    this.focusedIndex = index as number;
    onFocusIndexChanged?.(index as number, ...rest);
  };

  resetIndex = async (): Promise<void> => {
    return new Promise(res => {
      const { buffer } = this.props;
      this.focusedIndex = 0;
      this.setState(
        {
          isAnimating: false,
          left: 0,
          startIndex: 0,
          endIndex: buffer,
        },
        () => {
          const focusableItems = focusManager.getFocusableChildren(
            this.focusId,
          );
          const firstItem =
            focusableItems && focusableItems?.length > 0 ? focusableItems[0] : null;
          const firstItemId = focusManager.getElementFocusId(firstItem);
          focusManager.setCurrentFocus(firstItemId as string);
          res();
        },
      );
    });
  };

  getFocusedIndex = (): number => this.focusedIndex;

  render(): React.ReactNode {
    const { className, onFocusDelegated } = this.props;
    const { left, isAnimating } = this.state;
    const items = selectItems(this.props, this.state, this.listItem);

    return (
      <div className={className ?? undefined} ref={this.listViewport}>
        <FocusGroup
          className={classNames(
            {
              [styles.animate]: isAnimating && !device.isSlowDevice,
            },
            'text-nowrap',
          )}
          focusId={this.focusId ? this.focusId.toString() : undefined}
          onFocusDelegated={onFocusDelegated}
          onFocusIndexOverride={this.onFocusIndexOverride}
          onFocusIndexChanged={this.onFocusIndexChanged}
          ref={this.focusGroup}
          style={{
            transform: `translateX(${left / scaling}px)`,
            WebkitTransform: `translateX(${left / scaling}px)`,
          }}
          horizontal
          onEndOfNavigation={this.onEndOfNavigation}
        >
          {items}
        </FocusGroup>
      </div>
    );
  }
}

export default withRestoration<ListOwnProps & Record<string, unknown>>(props => props.restorationId)(List);
